// Based on https://github.com/jonbern/fetch-retry
import "cross-fetch/polyfill";

/**
 * @typedef {Function} RequestDelayFunction
 * @param {number} attempt
 * @param {Error | null} error
 * @param {Response | null} response
 * @returns {number}
 */

/**
 * @typedef {Function} RequestRetryOnFunction
 * @param {number} attempt
 * @param {Error | null} error
 * @param {Response | null} response
 * @returns {boolean}
 */

/**
 * @typedef {Object} IRequestInitWithRetry
 * @property {number} [retries]
 * @property {number | RequestDelayFunction} [retryDelay]
 * @property {number[] | RequestRetryOnFunction} [retryOn]
 */

/**
 * Fetch with retry and timeout capabilities
 * @param {string} url - The URL to fetch
 * @param {IRequestInitWithRetry} options - Fetch options with retry configuration
 * @param {number} timeout - Timeout in milliseconds (default: 5000)
 * @returns {Promise<Response>}
 */
export const fetchRetryTimeout = (url, options, timeout = 5000) => {
  let retries = 0;
  let retryDelay = 0;
  let retryOn = [];

  if (options?.retries !== undefined) {
    if (isPositiveInteger(options.retries)) {
      retries = options.retries;
    } else {
      throw new ArgumentError("retries must be a positive integer");
    }
  }

  if (options?.retryDelay !== undefined) {
    if (
      isPositiveInteger(options.retryDelay) ||
      typeof options.retryDelay === "function"
    ) {
      retryDelay = options.retryDelay;
    } else {
      throw new ArgumentError(
        "retryDelay must be a positive integer or a function returning a positive integer",
      );
    }
  }

  if (options?.retryOn) {
    if (
      Array.isArray(options.retryOn) ||
      typeof options.retryOn === "function"
    ) {
      retryOn = options.retryOn;
    } else {
      throw new ArgumentError("retryOn property expects an array or function");
    }
  }

  return new Promise((resolve, reject) => {
    const wrappedFetch = (attempt) => {
      timeoutFetch(url, options, timeout)
        .then((response) => {
          handleResponse(attempt, response, resolve, reject);
        })
        .catch((error) => {
          handleError(attempt, error, reject);
        });
    };

    function handleResponse(attempt, response, resolve, reject) {
      if (Array.isArray(retryOn) && !retryOn.includes(response.status)) {
        resolve(response);
      } else if (typeof retryOn === "function") {
        if (retryOn(attempt, null, response)) {
          scheduleRetry(attempt, null, response);
        } else {
          resolve(response);
        }
      } else if (attempt < retries) {
        scheduleRetry(attempt, null, response);
      } else {
        resolve(response);
      }
    }

    function handleError(attempt, error, reject) {
      if (typeof retryOn === "function") {
        if (retryOn(attempt, error, null)) {
          scheduleRetry(attempt, error, null);
        } else {
          reject(error);
        }
      } else if (attempt < retries) {
        scheduleRetry(attempt, error, null);
      } else {
        reject(error);
      }
    }

    function scheduleRetry(attempt, error, response) {
      const delay =
        typeof retryDelay === "function"
          ? retryDelay(attempt, error, response)
          : retryDelay;
      setTimeout(() => {
        wrappedFetch(++attempt);
      }, delay);
    }

    wrappedFetch(0);
  });
};

function timeoutFetch(url, options, timeout) {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new TimeoutError(`Request timed out after ${timeout}ms`)),
        timeout,
      ),
    ),
  ]);
}

/**
 * Check if value is a positive integer
 * @param {number} value
 * @returns {boolean}
 */
function isPositiveInteger(value) {
  return Number.isInteger(value) && value >= 0;
}

/**
 * ArgumentError class
 */
export class ArgumentError extends Error {
  constructor(message) {
    super(message);
    this.name = "ArgumentError";
  }
}

/**
 * TimeoutError class
 */
export class TimeoutError extends Error {
  constructor(message) {
    super(message);
    this.name = "TimeoutError";
  }
}
